import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Send, Upload, Copy, Mail, ShieldCheck, Clock,
  CheckCircle2, AlertCircle, Archive, Loader2, Download,
  Bell, BellOff, Volume2, VolumeX, History, XCircle, LogIn,
  MessageCircle, X, AlertTriangle, Sparkles,
} from "lucide-react";
import { api, formatApiError as formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EnablePushNotifications from "../components/care/EnablePushNotifications";
import { CarerReviewNotice } from "../components/shared/CarerReviewNotice";
import { CARER_REVIEW_PENDING_MESSAGE } from "../lib/carerReview";
import { useHandoverSession } from "../hooks/useHandoverSession";

const CHECKLIST_ORG_STATUSES = [
  { value: "not_discussed", label: "Not discussed", className: "bg-slate-100 text-ink-muted" },
  { value: "discussed", label: "Discussed", className: "bg-amber-50 text-amber-700" },
  { value: "needs_follow_up", label: "Needs follow-up", className: "bg-rose-50 text-rose-700" },
];

const STATUS_PILL = {
  discussion: "bg-teal-50 text-teal",
  activated: "bg-purple/10 text-purple",
  expired: "bg-slate-100 text-ink-muted",
  archived: "bg-slate-100 text-ink-muted",
};

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function formatTime(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function formatClock(ts) {
  try { return new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return ""; }
}

/** One-line label for a room event (activation, archive, status change etc). */
function renderEventLine(e) {
  const clock = formatTime(e.timestamp);
  const who = e.actor_name || e.actor_email || "Someone";
  const statusLabels = {
    activated: "Active", under_review: "Under Review", ending_soon: "Ending Soon",
    archived: "Archived", discussion: "Discussion",
  };
  switch (e.event_type) {
    case "activated":
      return `Care Partnership Room activated by ${who} at ${clock}`;
    case "status_changed":
      return `${who} moved this room to ${statusLabels[e.to_status] || e.to_status} at ${clock}`;
    case "archived":
      return `${who} archived this Care Discussion Room at ${clock} · read-only for both parties`;
    default:
      return `${who} · ${e.event_type} · ${clock}`;
  }
}

/** Human-readable label for a room status value. */
function statusLabel(s) {
  return ({
    discussion: "Discussion", activated: "Active", under_review: "Under Review",
    ending_soon: "Ending Soon", archived: "Archived", expired: "Expired",
  })[s] || s;
}

const FOLDER_META = {
  agreements_and_terms:       { label: "Agreements & Terms",        order: 1 },
  communications:             { label: "Communications",            order: 2 },
  shift_kit_records:          { label: "Shift Kit Records",         order: 3 },
  emergency_incident_records: { label: "Emergency / Incident Records", order: 4 },
  other_documents:            { label: "Other Documents",           order: 5 },
  archive:                    { label: "Archive",                   order: 6 },
};
const FOLDER_ORDER = Object.entries(FOLDER_META)
  .sort((a, b) => a[1].order - b[1].order).map(([k]) => k);

const SHIFT_KIT_NOTICE = (
  "This folder is for practical, non-clinical Shift Kit records only. " +
  "Please do not upload detailed medical information, medication records, " +
  "safeguarding reports, GP or hospital letters, or other detailed health " +
  "records. Keep sensitive health information in the appropriate care or " +
  "health record system."
);

const FC_SAFETY_TITLE = "Do not upload medical or clinical records";
const FC_SAFETY_BODY = (
  "The Filing Cabinet is for administrative documents connected with the " +
  "care arrangement, such as agreements, schedules, invoices and general " +
  "placement documents. Do not upload medical records, medication " +
  "records, clinical care plans, diagnoses, hospital letters, test " +
  "results, photographs of injuries or detailed notes about a person\u2019s " +
  "physical or mental health. JCC is not a medical-record system, and " +
  "Care Rooms are not monitored by JCC. Urgent medical concerns, " +
  "safeguarding concerns and emergencies must be reported through the " +
  "appropriate professional or emergency channels."
);
const FC_SAFETY_CHECKBOX_LABEL = (
  "I confirm this document does not contain medical or clinical records " +
  "and that I am authorised to share it."
);
function daysRemaining(iso) {
  try {
    const diff = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  } catch { return null; }
}

/** Play a gentle two-tone chime (~350ms) to signal a new message or event
 *  from the other person. Uses the Web Audio API so we ship no audio asset.
 *  Tuned to be clearly audible but calm — not alarming. Fully guarded —
 *  swallowed if the browser blocks unattended audio. */
function playNotificationChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    // Two tones, gentle major third: G5 (784Hz) followed by E5 (659Hz).
    // Exponential decay + moderate peak gain (~0.17) so it's noticeable
    // on laptops without being intrusive.
    const tone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.17, now + start + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + duration + 0.02);
    };
    tone(784, 0.00, 0.22);   // G5
    tone(659, 0.14, 0.24);   // E5
    // Close the audio context after the chime finishes to free resources.
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 500);
  } catch { /* audio blocked — silently ignore */ }
}

// ──────────────────────────────────────────────────────────────────────
// Section panels are kept inline below to keep this MVP a single file.
// ──────────────────────────────────────────────────────────────────────

function Panel({ title, eyebrow, children, testid }) {
  return (
    <section className="card-base p-6 md:p-7" data-testid={testid}>
      {eyebrow && <div className="overline text-teal mb-1">{eyebrow}</div>}
      {title && <h2 className="font-display text-xl md:text-2xl font-semibold leading-tight">{title}</h2>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function StatusPill({ status }) {
  return (
    <span className={`pill ${STATUS_PILL[status] || "bg-slate-100 text-ink-muted"}`} data-testid="cdr-status-pill">
      {status}
    </span>
  );
}

const AGREEMENT_STATE_COPY = {
  awaiting_confirmation: "Awaiting confirmation",
  changed_awaiting_reconfirmation: "Changed — confirmation required",
  legacy_unconfirmed: "Previously marked Agreed — confirmation required",
};

function ChecklistItemRow({ item, role, readOnly, busy, draftValue, onDraftChange,
                            onOrgStatus, onPropose, onConfirm, onDispute, onHistory }) {
  const [editing, setEditing] = useState(false);
  const state = item.agreement_state || "none";
  const proposerKnown = !!item.proposed_by_role;
  const isProposer = proposerKnown && item.proposed_by_role === role;
  const pendingStates = ["awaiting_confirmation", "changed_awaiting_reconfirmation", "legacy_unconfirmed"];
  const showInput = state === "none" || (isProposer && pendingStates.includes(state))
    || (!proposerKnown && pendingStates.includes(state)) || (state === "agreed_by_both" && editing);
  const value = draftValue ?? item.agreed_detail ?? "";

  return (
    <li className="py-3 border-b border-slate-100 last:border-0" data-testid={`cdr-check-${item.item_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex-1 text-sm font-medium">{item.label}</span>
        <div className="flex flex-wrap gap-1.5">
          {CHECKLIST_ORG_STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              disabled={readOnly || busy}
              onClick={() => onOrgStatus(item, s.value)}
              data-testid={`checklist-status-${s.value}-${item.item_id}`}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                item.status === s.value ? s.className + " ring-1 ring-current" : "text-ink-soft hover:bg-slate-100"
              } ${readOnly || busy ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {s.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onHistory(item)}
            className="text-xs px-2 py-1 rounded-full text-ink-soft hover:bg-slate-100 inline-flex items-center gap-1"
            data-testid={`checklist-history-btn-${item.item_id}`}
          >
            <History className="w-3 h-3" /> View history
          </button>
        </div>
      </div>

      {state !== "none" && (
        <div className="mt-2 text-xs" data-testid={`checklist-agreement-state-${item.item_id}`}>
          {state === "agreed_by_both" ? (
            <span className="pill bg-teal-50 text-teal">✓ Agreed by both</span>
          ) : (
            <span className="pill bg-amber-50 text-amber-700">{AGREEMENT_STATE_COPY[state]}</span>
          )}
        </div>
      )}

      {!readOnly && showInput && (
        <div className="mt-2.5 flex flex-wrap items-end gap-2">
          <textarea
            rows={1}
            value={value}
            onChange={(e) => onDraftChange(item.item_id, e.target.value)}
            placeholder={item.agreement_detail_required ? "What did you agree? (required)" : "What did you agree? (optional)"}
            className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/30"
            data-testid={`checklist-detail-input-${item.item_id}`}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => { onPropose(item); setEditing(false); }}
            className="btn-teal !py-1.5 !px-3 text-xs"
            data-testid={`checklist-propose-btn-${item.item_id}`}
          >
            {state === "none" ? "Mark as agreed" : "Update proposal"}
          </button>
        </div>
      )}

      {!readOnly && !isProposer && pendingStates.includes(state) && (proposerKnown || state === "legacy_unconfirmed") && (
        <div className="mt-2.5 space-y-1.5">
          {item.agreed_detail && (
            <p className="text-sm text-ink-muted" data-testid={`checklist-proposed-detail-${item.item_id}`}>
              &ldquo;{item.agreed_detail}&rdquo;
            </p>
          )}
          <p className="text-xs text-ink-soft">
            {proposerKnown ? `The other participant marked this as agreed.` : "This was previously marked Agreed — please confirm or dispute it."}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => onConfirm(item)}
              className="btn-teal !py-1.5 !px-3 text-xs"
              data-testid={`checklist-confirm-btn-${item.item_id}`}
            >
              Confirm agreement
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onDispute(item)}
              className="btn-outline !py-1.5 !px-3 text-xs"
              data-testid={`checklist-dispute-btn-${item.item_id}`}
            >
              Needs follow-up
            </button>
          </div>
        </div>
      )}

      {state === "agreed_by_both" && !editing && !readOnly && (
        <div className="mt-2 flex items-center gap-2">
          {item.agreed_detail && (
            <p className="text-sm text-ink-muted flex-1" data-testid={`checklist-agreed-detail-${item.item_id}`}>
              &ldquo;{item.agreed_detail}&rdquo;
            </p>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-ink-soft underline hover:text-ink"
            data-testid={`checklist-edit-btn-${item.item_id}`}
          >
            Edit
          </button>
        </div>
      )}
      {state === "agreed_by_both" && readOnly && item.agreed_detail && (
        <p className="mt-2 text-sm text-ink-muted">&ldquo;{item.agreed_detail}&rdquo;</p>
      )}
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────

export default function CareDiscussionRoomDetail() {
  const { roomId } = useParams();
  const { user } = useAuth();
  const session = useHandoverSession();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reviewPending, setReviewPending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draftMsg, setDraftMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [decisionForm, setDecisionForm] = useState({ topic: "", decision: "", agreed_by: "" });
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState("other_documents");
  const [fcSafetyAck, setFcSafetyAck] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [ackForm, setAckForm] = useState({ user_full_name: "", read_confirmation: false, typed_ack_text: "" });
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [sendCopyToFamily, setSendCopyToFamily] = useState(false);
  const [inviteHistory, setInviteHistory] = useState(null);   // family-only timeline
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [activating, setActivating] = useState(false);

  // Notifications: unread count for the chat panel, soft toast when messages
  // arrive from the other person, and an optional sound (default OFF,
  // persisted per-browser in localStorage).
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return window.localStorage.getItem("jcc_cdr_sound") === "on"; }
    catch { return false; }
  });
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  const toggleSound = () => setSoundEnabled((prev) => {
    const next = !prev;
    try { window.localStorage.setItem("jcc_cdr_sound", next ? "on" : "off"); }
    catch { /* storage unavailable */ }
    return next;
  });

  // Auto-save reassurance timestamps — surfaced next to each editable
  // section so both users can see that their input has landed on the
  // server. Backend already persists everything; this is UX-only.
  const [savedAt, setSavedAt] = useState({ chat: null, checklist: null, decisions: null });
  const markSaved = useCallback((key) => {
    setSavedAt((s) => ({ ...s, [key]: Date.now() }));
  }, []);

  // Refs used by side-effects
  const msgEndRef = useRef(null);
  const msgListRef = useRef(null);          // inner scroll container
  const msgSectionRef = useRef(null);       // whole chat panel (for visibility check)
  const knownMsgIdsRef = useRef(new Set()); // seen message_ids so we can detect new arrivals
  const dataRef = useRef(null);             // mirrors last-observed room payload for transition detection
  const chatVisibleRef = useRef(true);      // set by IntersectionObserver
  const initialMsgLoadRef = useRef(true);   // suppress toasts on first /messages fetch
  const initialFullLoadRef = useRef(true);  // suppress toasts on first /room fetch
  const knownEventIdsRef = useRef(new Set()); // seen room event_ids so we can toast on new ones

  // Floating chat launcher visibility & modal open state. The launcher
  // appears when the Discussion Log section has scrolled out of view; the
  // modal renders the SAME messages + sendMessage handler as the main
  // section — no second chat, no duplicate storage.
  const [chatOffScreen, setChatOffScreen] = useState(false);
  const [showFloatingChat, setShowFloatingChat] = useState(false);
  const floatingInputRef = useRef(null);

  // Load the full room (and refresh messages) on mount and whenever roomId or
  // refreshKey changes. A 8s polling interval keeps the discussion log fresh
  // without needing websockets. One consolidated effect avoids stale-closure
  // pitfalls and chained-state-setting in effect lint warnings.
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshRoom = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Checklist state — must be declared before any conditional early returns
  // below (Rules of Hooks). Handlers that use these are further down.
  const [checklistDraftDetail, setChecklistDraftDetail] = useState({});
  const [checklistBusy, setChecklistBusy] = useState({});
  const [historyItem, setHistoryItem] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const myEmail = (user?.email || "").toLowerCase();
    const myRole = dataRef.current?.room?.your_role;

    const fetchFull = async () => {
      try {
        const { data: roomData } = await api.get(`/api/care-rooms/${roomId}`);
        if (cancelled) return;
        // Detect carer FIRST-ENTRY by comparing the last-observed room
        // payload (dataRef) against the freshly-fetched one. Using a
        // data-mirror ref rather than an isolated carerFirstSeenRef
        // avoids a subtle StrictMode / double-invocation timing gap
        // where two independent refs could get out of sync.
        const prevSeen = dataRef.current?.room?.carer_first_seen_at || null;
        const nextSeen = roomData?.room?.carer_first_seen_at || null;
        const isFamily = roomData?.room?.your_role === "family";
        const carerJustEntered =
          !initialFullLoadRef.current && isFamily && nextSeen && !prevSeen;
        if (carerJustEntered) {
          const who = roomData?.room?.carer_name || roomData?.room?.carer_email || "The carer";
          toast.message(`${who} has joined the discussion`, {
            description: "They can now see the room and reply.",
          });
          if (soundEnabledRef.current) playNotificationChime();
        }
        dataRef.current = roomData;
        initialFullLoadRef.current = false;
        setData(roomData);
        setError(null);

        // Refresh invitation history from inside fetchFull so it stays
        // live with every 8s poll. Family/admin only — no leak to carer.
        if (isFamily || roomData?.room?.your_role === "admin") {
          try {
            const { data: hist } = await api.get(`/api/care-rooms/${roomId}/invites`);
            if (!cancelled) setInviteHistory(hist);
          } catch { /* non-blocking */ }
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403 && err?.response?.data?.detail === CARER_REVIEW_PENDING_MESSAGE) {
          setReviewPending(true);
        }
        setError(status === 403 ? "forbidden" : status === 404 ? "notfound" : "error");
      }
    };

    const fetchMessages = async () => {
      try {
        const { data: msgData } = await api.get(`/api/care-rooms/${roomId}/messages`);
        if (cancelled) return;
        const newMsgs = msgData.messages || [];
        const known = knownMsgIdsRef.current;

        // On the very first successful load, seed the "known" set silently
        // so we don't fire a toast for every historical message.
        if (initialMsgLoadRef.current) {
          newMsgs.forEach((m) => known.add(m.message_id));
          initialMsgLoadRef.current = false;
          setMessages(newMsgs);
          return;
        }

        // Detect NEW messages authored by the OTHER party since the last poll.
        // We double-check with sender_role vs your_role too, because handover
        // users have empty user?.email (AuthContext is JWT-only), so the
        // sender_email compare alone would let self-sent messages slip
        // through in a race between POST and the next poll.
        const currentRole = dataRef.current?.room?.your_role || myRole;
        const arrivedFromOther = newMsgs.filter(
          (m) => !known.has(m.message_id) &&
                 (m.sender_email || "").toLowerCase() !== myEmail &&
                 (!currentRole || m.sender_role !== currentRole)
        );
        newMsgs.forEach((m) => known.add(m.message_id));
        setMessages(newMsgs);

        if (arrivedFromOther.length > 0) {
          // Toast + unread badge — only if the user is NOT currently
          // looking at the chat panel.
          const visible = chatVisibleRef.current;
          if (!visible) {
            const first = arrivedFromOther[0];
            const who = first.sender_name || first.sender_email || "The other person";
            toast.message(
              arrivedFromOther.length === 1
                ? `New message from ${who}`
                : `${arrivedFromOther.length} new messages from ${who}`,
              { description: first.body?.slice(0, 90) }
            );
            setUnreadCount((n) => n + arrivedFromOther.length);
            if (soundEnabledRef.current) playNotificationChime();
          }
        }
      } catch (_e) {
        // Polling errors are intentionally swallowed — the user keeps the
        // previously-loaded messages on transient network blips.
      }
    };

    fetchFull();
    fetchMessages();
    // Poll BOTH endpoints on the same interval — the room object carries
    // carer_first_seen_at / activation / status changes that must be
    // picked up live. Previously only /messages was polled and the
    // "carer has joined" toast only fired on a manual refresh.
    const intervalId = setInterval(() => {
      fetchFull();
      fetchMessages();
    }, 8000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [roomId, refreshKey, user?.email]);

  // Combined chronological timeline of messages + system events, used by
  // both the main Discussion Log AND the floating chat modal so the two
  // views cannot drift. (Hoisted above the useEffects that read it to
  // avoid the TDZ crash "Cannot access 'timeline' before initialization".)
  const timeline = useMemo(() => {
    const items = [];
    messages.forEach((m) => items.push({ kind: "msg", at: m.created_at, payload: m }));
    (data?.events || []).forEach((e) => items.push({ kind: "event", at: e.timestamp, payload: e }));
    items.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    return items;
  }, [messages, data?.events]);

  // Scroll only the INNER message list to its bottom whenever the list grows.
  // Previous implementation called scrollIntoView on a sentinel element,
  // which forced the browser to scroll the entire page down past the
  // discussion log — the user then had to hunt the chat back manually.
  useEffect(() => {
    const el = msgListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [timeline.length]);

  // Observe whether the chat panel is currently in the viewport. When it
  // scrolls into view, treat all pending messages as read. Track the
  // opposite state (`chatOffScreen`) so the floating-chat launcher can
  // show itself only when the main Discussion Log is not visible.
  useEffect(() => {
    const el = msgSectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          chatVisibleRef.current = entry.isIntersecting;
          setChatOffScreen(!entry.isIntersecting);
          if (entry.isIntersecting) setUnreadCount(0);
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [data]);

  // Detect NEW room events (e.g. archive) since the last poll, and fire a
  // gentle toast so the remaining participant is told. Seeded silently on
  // first load — historic events do NOT re-toast.
  useEffect(() => {
    const events = data?.events || [];
    const known = knownEventIdsRef.current;
    const isFirstLoad = known.size === 0 && events.length > 0 && !dataRef.current?.events;
    if (isFirstLoad) {
      events.forEach((e) => known.add(e.event_id));
      return;
    }
    // Use server-computed role for the self-actor filter rather than
    // AuthContext.user.email — the CDR is accessed via the magic-link
    // handover session, which AuthContext does not know about (so
    // user.email would be empty and the actor would incorrectly get
    // toasted about their own action). `your_role` is set server-side
    // and correct for both JWT and handover users.
    const myRole = data?.room?.your_role;
    const newEvents = events.filter((e) => !known.has(e.event_id));
    newEvents.forEach((e) => known.add(e.event_id));
    newEvents.forEach((e) => {
      // Skip events initiated by the current user — they triggered the
      // action from their own screen and don't need a system toast.
      if (myRole && e.actor_role === myRole) return;
      if (e.event_type === "archived") {
        toast.message(
          `${e.actor_name} archived this Care Discussion Room`,
          { description: "It is now read-only for both parties." }
        );
        if (soundEnabledRef.current) playNotificationChime();
      }
    });
  }, [data?.events, data?.room?.your_role]);

  // Family/admin invite history is now refreshed inside fetchFull() alongside
  // the room object — this keeps cdr-invite-history-carer-entered live within
  // one polling cycle. See fetchFull above.

  if (error === "forbidden" && reviewPending) {
    return (
      <div className="container-jcc py-24 max-w-xl text-center" data-testid="cdr-review-pending">
        <CarerReviewNotice email={user?.email} className="text-left" />
        <Link to="/care-rooms" className="btn-outline mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> My rooms
        </Link>
      </div>
    );
  }

  if (error === "forbidden") {
    return (
      <div className="container-jcc py-24 max-w-xl text-center" data-testid="cdr-forbidden">
        <ShieldCheck className="w-12 h-12 text-ink-soft mx-auto" />
        <h1 className="font-display text-3xl font-bold mt-4">This room is private</h1>
        <p className="mt-3 text-ink-muted">
          Only the carer and family/client representative listed on this room can access it.
          {user?.email && <> You&apos;re signed in as <strong>{user.email}</strong>.</>}
        </p>
        <p className="mt-3 text-ink-muted text-sm">
          If you were invited to this room, please log in with the same email address used in the invitation.
        </p>
        <Link to="/care-rooms" className="btn-outline mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> My rooms
        </Link>
      </div>
    );
  }

  if (error === "notfound") {
    return (
      <div className="container-jcc py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Room not found</h1>
        <Link to="/care-rooms" className="btn-outline mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> My rooms
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container-jcc py-24 text-center text-ink-muted">
        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading room…
      </div>
    );
  }

  const { room, checklist, decisions, documents } = data;
  const acknowledgements = data.acknowledgements || [];
  const role = room.your_role;
  const readOnly = ["expired", "archived"].includes(room.status) && role !== "admin";
  const remaining = daysRemaining(room.expires_at);

  // Phase 1 activated-room extension: any status past `discussion` unlocks
  // the Filing Cabinet, Acknowledgement, message-importance flag, folder
  // groupings and placement-state controls. Discussion-phase rooms remain
  // exactly as they were before this build.
  const isActivated = ["activated", "under_review", "ending_soon", "archived"].includes(room.status);

  const isFamilyCircle = session.ready && session.entitlements.includes("family_circle");
  const showArrangementBanner = role === "family" && isActivated;
  const showCarerArrangementBanner = role === "carer" && isActivated;

  // Documents uploaded BEFORE activation form a read-only "Original
  // Discussion Documents" grouping to preserve the historic record.
  const activatedAt = room.activated_at ? new Date(room.activated_at).getTime() : null;
  const isPreActivationDoc = (d) =>
    activatedAt && new Date(d.uploaded_at).getTime() < activatedAt;

  // ── Discussion log ────────────────────────────────────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!draftMsg.trim() || sending) return;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/api/care-rooms/${roomId}/messages`, { body: draftMsg.trim() });
      setMessages((m) => {
        const next = [...m, msg];
        knownMsgIdsRef.current.add(msg.message_id);
        return next;
      });
      setDraftMsg("");
      markSaved("chat");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSending(false);
    }
  };

  // Toggle "Mark as important" on any message (any participant, any role).
  const toggleImportant = async (msg) => {
    try {
      const { data: updated } = await api.patch(
        `/api/care-rooms/${roomId}/messages/${msg.message_id}`,
        { is_important: !msg.is_important }
      );
      setMessages((list) => list.map((m) =>
        m.message_id === updated.message_id ? { ...m, ...updated } : m
      ));
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  // ── Filing Cabinet document actions ──────────────────────────
  const moveDocument = async (doc, folder) => {
    try {
      const { data: updated } = await api.patch(
        `/api/care-rooms/${roomId}/documents/${doc.document_id}`,
        { folder }
      );
      setData((d) => ({
        ...d,
        documents: d.documents.map((x) =>
          x.document_id === updated.document_id ? { ...x, ...updated } : x
        ),
      }));
      toast.success(folder === "archive" ? "Document archived" : "Document moved");
    } catch (err) { toast.error(formatErr(err)); }
  };

  // ── Placement state (post-activation) ─────────────────────────
  const changeStatus = async (nextStatus) => {
    if (statusChanging) return;
    if (nextStatus === "archived" && !window.confirm(
      "Archive this Care Partnership Room? It will become read-only for both parties."
    )) return;
    setStatusChanging(true);
    try {
      await api.post(`/api/care-rooms/${roomId}/status`, { status: nextStatus });
      refreshRoom();
      toast.success(`Room status updated`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setStatusChanging(false); }
  };

  // ── Shared Agreement acknowledgement ─────────────────────────
  const submitAcknowledgement = async (e) => {
    e.preventDefault();
    if (ackSubmitting) return;
    if (!ackForm.read_confirmation) { toast.error("Please tick the confirmation box"); return; }
    if (!ackForm.user_full_name.trim() || !ackForm.typed_ack_text.trim()) return;
    setAckSubmitting(true);
    try {
      await api.post(`/api/care-rooms/${roomId}/acknowledgements`, {
        user_full_name: ackForm.user_full_name.trim(),
        read_confirmation: true,
        typed_ack_text: ackForm.typed_ack_text.trim(),
        agreement_version: "v1",
      });
      setAckForm({ user_full_name: "", read_confirmation: false, typed_ack_text: "" });
      refreshRoom();
      toast.success("Acknowledgement recorded");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setAckSubmitting(false); }
  };

  // ── Checklist ─────────────────────────────────────────────────
  const setBusy = (itemId, val) => setChecklistBusy((b) => ({ ...b, [itemId]: val }));

  const applyChecklistUpdate = (updated) => {
    setData((d) => ({
      ...d,
      checklist: d.checklist.map((c) => (c.item_id === updated.item_id ? updated : c)),
    }));
    markSaved("checklist");
  };

  const handleChecklistConflict = (err) => {
    if (err?.response?.status === 409) {
      toast.error("This item was just updated by the other participant — refreshed.");
      refreshRoom();
      return true;
    }
    return false;
  };

  const updateOrgStatus = async (item, status) => {
    setBusy(item.item_id, true);
    try {
      const { data: updated } = await api.patch(
        `/api/care-rooms/${roomId}/checklist/${item.item_id}`,
        { status, expected_version: item.version }
      );
      applyChecklistUpdate(updated);
    } catch (err) {
      if (!handleChecklistConflict(err)) toast.error(formatErr(err));
    } finally {
      setBusy(item.item_id, false);
    }
  };

  const proposeAgreement = async (item) => {
    const detail = (checklistDraftDetail[item.item_id] ?? item.agreed_detail ?? "").trim();
    if (item.agreement_detail_required && !detail) {
      toast.error("Please add what you agreed for this item.");
      return;
    }
    setBusy(item.item_id, true);
    try {
      const { data: updated } = await api.post(
        `/api/care-rooms/${roomId}/checklist/${item.item_id}/propose-agreement`,
        { agreed_detail: detail || null, expected_version: item.version }
      );
      applyChecklistUpdate(updated);
      setChecklistDraftDetail((d) => ({ ...d, [item.item_id]: undefined }));
      toast.success("Proposed — waiting on the other participant to confirm.");
    } catch (err) {
      if (!handleChecklistConflict(err)) toast.error(formatErr(err));
    } finally {
      setBusy(item.item_id, false);
    }
  };

  const confirmAgreement = async (item) => {
    setBusy(item.item_id, true);
    try {
      const { data: updated } = await api.post(
        `/api/care-rooms/${roomId}/checklist/${item.item_id}/confirm-agreement`,
        { expected_version: item.version }
      );
      applyChecklistUpdate(updated);
      toast.success("Confirmed — Agreed by both.");
    } catch (err) {
      if (!handleChecklistConflict(err)) toast.error(formatErr(err));
    } finally {
      setBusy(item.item_id, false);
    }
  };

  const disputeAgreement = (item) => updateOrgStatus(item, "needs_follow_up");

  const openHistory = async (item) => {
    setHistoryItem(item);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/api/care-rooms/${roomId}/checklist/${item.item_id}/history`);
      setHistoryRows(data.history || []);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Decisions ─────────────────────────────────────────────────
  const addDecision = async (e) => {
    e.preventDefault();
    if (!decisionForm.topic.trim()) {
      toast.error("Please add a topic before recording this decision.");
      return;
    }
    if (!decisionForm.decision.trim()) {
      toast.error("Please add a decision summary before recording this decision.");
      return;
    }
    try {
      const { data: created } = await api.post(`/api/care-rooms/${roomId}/decisions`, decisionForm);
      setData((d) => ({ ...d, decisions: [created, ...d.decisions] }));
      setDecisionForm({ topic: "", decision: "", agreed_by: "" });
      markSaved("decisions");
      toast.success("Decision recorded");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  // ── Documents ─────────────────────────────────────────────────
  const uploadDoc = async (file, title) => {
    if (!file) return;
    if (!fcSafetyAck) {
      toast.error("Please confirm the document does not contain medical or clinical records before uploading.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB limit");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      // Only pass a folder when the room is activated — pre-activation
      // uploads land in the default 'other_documents' bucket, unchanged.
      if (isActivated) fd.append("folder", uploadFolder);
      const { data: created } = await api.post(`/api/care-rooms/${roomId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData((d) => ({ ...d, documents: [created, ...d.documents] }));
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(
        `/api/care-rooms/${roomId}/documents/${doc.document_id}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename || doc.title || "document";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  // ── Invite ────────────────────────────────────────────────────
  const generateInvite = async () => {
    try {
      const { data } = await api.post(
        `/api/care-rooms/${roomId}/invite`,
        { send_copy_to_family: sendCopyToFamily }
      );
      setInvite(data);
      if (data.email_sent) toast.success("Invitation sent to the carer");
      else toast.error("Email invitation not sent — see details below");
      // Refresh the invite-history timeline so the family sees the new entry.
      try {
        const { data: hist } = await api.get(`/api/care-rooms/${roomId}/invites`);
        setInviteHistory(hist);
      } catch { /* non-blocking */ }
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  // ── Activation ────────────────────────────────────────────────
  // Security: only the family/client representative on this room, or an
  // admin, can flip the room to "activated". The carer is blocked by the
  // backend (403). Backend authorisation is scoped by email match on
  // family_email, so the family caller can only ever activate their own
  // room — the UI mirrors that by only rendering the button for family
  // and admin roles.
  const confirmActivation = async () => {
    if (role !== "family" && role !== "admin") return;
    setActivating(true);
    try {
      await api.post(`/api/care-rooms/${roomId}/activate`);
      toast.success("Care Partnership Room activated");
      refreshRoom();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setActivating(false);
      setShowActivateConfirm(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="bg-surface min-h-screen" data-testid="page-cdr-detail">
      <div className="container-jcc py-10 md:py-14 max-w-5xl">
        <div className="mb-6">
          <Link to="/care-rooms" className="btn-outline !px-4 !py-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> My rooms
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <StatusPill status={room.status} />
            <span className="pill bg-slate-100 text-ink-muted" data-testid="cdr-role">Your role: {role}</span>
            {readOnly && <span className="pill bg-rose-50 text-rose-700">Read-only</span>}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            Care Discussion Room
          </h1>
          <p className="mt-2 text-ink-muted">
            Client: <strong>{room.client_label}</strong>
          </p>
        </div>

        {showArrangementBanner && (
          <div className="mb-6 rounded-2xl border border-purple/20 bg-purple/5 p-5 md:p-6 flex flex-wrap items-center gap-4 justify-between" data-testid="arrangement-record-banner">
            <div className="flex items-start gap-3 max-w-xl">
              <Sparkles className="w-5 h-5 text-purple shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
              <div>
                <p className="font-medium text-ink">
                  Decided to go ahead?
                </p>
                <p className="text-sm text-ink-muted mt-1">
                  {isFamilyCircle
                    ? "You've talked it through. You like this carer. You want to go ahead. What do you do now?"
                    : "Family Care Office members can turn agreed details into an organised Care Arrangement, so both sides have a clear reference point."}
                </p>
              </div>
            </div>
            {isFamilyCircle ? (
              <Link
                to={`/workspace/family-circle/arrangement/${roomId}`}
                className="btn-purple text-sm whitespace-nowrap"
                data-testid="cdr-arrangement-record-cta"
              >
                Set up your care arrangement
              </Link>
            ) : (
              <Link
                to="/family-access"
                className="btn-outline text-sm whitespace-nowrap"
                data-testid="cdr-arrangement-record-upsell"
              >
                About Family Care Office
              </Link>
            )}
          </div>
        )}

        {showCarerArrangementBanner && (
          <div className="mb-6 rounded-2xl border border-purple/20 bg-purple/5 p-5 md:p-6 flex flex-wrap items-center gap-4 justify-between" data-testid="carer-arrangement-links-banner">
            <div className="flex items-start gap-3 max-w-xl">
              <Sparkles className="w-5 h-5 text-purple shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
              <div>
                <p className="font-medium text-ink">Your Care Arrangement</p>
                <p className="text-sm text-ink-muted mt-1">
                  Read the terms this family has recorded for your Room, and check for any shared care information they've given you.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to={`/care-discussion-room/${roomId}/my-arrangement`} className="btn-purple text-sm whitespace-nowrap" data-testid="cdr-my-arrangement-link">
                View my Care Arrangement
              </Link>
              <Link to={`/care-discussion-room/${roomId}/shared-info`} className="btn-outline text-sm whitespace-nowrap" data-testid="cdr-shared-info-link">
                Getting Ready for Care
              </Link>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* 0. Notifications on this device */}
          <div data-testid="cdr-push-controls-slot">
            <EnablePushNotifications roomId={roomId} />
          </div>

          {/* 1. Welcome */}
          <Panel eyebrow="1 · Welcome" title="A shared discussion space" testid="cdr-section-welcome">
            <p className="text-ink-muted leading-relaxed">
              This temporary Care Discussion Room helps a self-employed carer and a
              family/client representative discuss expectations, agree key terms and decide
              whether to activate a full Care Partnership Room. Once both parties agree to
              work together, the family/client can activate the full Care Partnership Room to
              continue using shared records, documents, care logs and communication tools.
            </p>
            <p className="mt-4 text-ink-muted leading-relaxed">
              WhatsApp and Messenger are useful for quick messages, but they were not built
              for private care arrangements. Your Care Partnership Room is different.
              Communications are automatically recorded and time-stamped, and important
              records are kept in one organised place so they cannot be casually deleted,
              changed, or buried in a busy chat thread. This gives both the carer and the
              family a clearer shared record if questions, concerns or emergencies arise.
            </p>
          </Panel>

          {/* 2. Participants */}
          <Panel eyebrow="2 · Participants" title="Who's in this room" testid="cdr-section-participants">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Client</div><div className="mt-1 font-medium">{room.client_label}</div></div>
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Proposed start</div><div className="mt-1 font-medium">{room.proposed_start_date || "—"}</div></div>
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Carer</div><div className="mt-1 font-medium">{room.carer_name}</div><div className="text-xs text-ink-soft">{room.carer_email}</div></div>
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Family / client representative</div><div className="mt-1 font-medium">{room.family_name || "—"}</div><div className="text-xs text-ink-soft">{room.family_email}</div></div>
            </div>

            {role === "family" && (
              <div className="mt-6 pt-5 border-t border-slate-200">
                <label
                  className="flex items-start gap-2.5 text-sm text-ink-muted mb-3 cursor-pointer select-none"
                  data-testid="cdr-invite-copy-toggle"
                >
                  <input
                    type="checkbox"
                    checked={sendCopyToFamily}
                    onChange={(e) => setSendCopyToFamily(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-teal focus:ring-teal"
                    data-testid="cdr-invite-copy-checkbox"
                  />
                  <span>
                    Send me a copy of this invitation
                    <span className="block text-xs text-ink-soft mt-0.5">
                      A copy of the email will also be sent to your address for your own records.
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  onClick={generateInvite}
                  className="btn-outline"
                  data-testid="cdr-invite-btn"
                >
                  <Mail className="w-4 h-4" /> Invite carer
                </button>
                {invite && (
                  <div className="mt-4 space-y-3" data-testid="cdr-invite-output">
                    {invite.email_sent ? (
                      <div className="rounded-xl bg-teal-50 border border-teal/30 p-3 text-sm text-teal flex items-start gap-2"
                           data-testid="cdr-invite-email-sent">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>Invitation sent to <strong>{invite.carer_email}</strong>. It may take a minute to arrive.</span>
                      </div>
                    ) : (
                      <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800 flex items-start gap-2"
                           data-testid="cdr-invite-email-failed">
                        <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-medium">Email invitation not sent</div>
                          <div className="mt-1 text-rose-700">
                            We couldn&rsquo;t send the email invitation this time.
                            The room has still been created, and you can copy the
                            private invite link below and send it to the carer manually.
                          </div>
                        </div>
                      </div>
                    )}
                    {invite.family_copy_requested && invite.family_copy_sent && (
                      <p className="text-sm text-teal" data-testid="cdr-invite-family-copy-sent">
                        ✓ Copy sent to you for your records
                      </p>
                    )}
                    {invite.family_copy_requested && !invite.family_copy_sent && (
                      <p className="text-sm text-amber-700" data-testid="cdr-invite-family-copy-failed">
                        Copy to yourself couldn&rsquo;t be sent — you can still copy the message below.
                      </p>
                    )}
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-ink-soft mb-1">Private invitation link</label>
                      <div className="flex gap-2">
                        <input readOnly value={invite.link} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50" />
                        <button onClick={() => copyText(invite.link)} className="btn-outline !px-3 !py-2" type="button">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-ink-soft mb-1">Copyable message</label>
                      <textarea readOnly value={invite.message} rows={6} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50 font-mono" />
                      <button onClick={() => copyText(invite.message)} className="btn-outline mt-2 text-sm" type="button">
                        <Copy className="w-4 h-4" /> Copy message
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* 2b. Invitation history — family only, chronological timeline */}
          {(role === "family" || role === "admin") && inviteHistory && (
            <Panel
              eyebrow="Invitation history"
              title="Who has been invited and when they entered"
              testid="cdr-section-invite-history"
            >
              {(inviteHistory.invites || []).length === 0 &&
               !inviteHistory.carer_first_seen_at ? (
                <p className="text-sm text-ink-soft">
                  No invitations sent yet. Use &ldquo;Invite carer&rdquo; above to send one.
                </p>
              ) : (
                <ol className="space-y-3" data-testid="cdr-invite-history-list">
                  {(inviteHistory.invites || []).map((it) => (
                    <li
                      key={it.invite_id}
                      className="flex items-start gap-3"
                      data-testid={`cdr-invite-history-item-${it.invite_id}`}
                    >
                      {it.delivery_status === "sent" ? (
                        <CheckCircle2 className="w-5 h-5 text-teal mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
                      )}
                      <div className="text-sm">
                        <div>
                          <strong>
                            {it.delivery_status === "sent"
                              ? "Invitation sent"
                              : "Invitation failed"}
                          </strong>{" "}
                          to {it.carer_email}
                        </div>
                        <div className="text-xs text-ink-soft">
                          {formatTime(it.sent_at)}
                          {it.family_copy_requested && (
                            <> · Copy to you:{" "}
                              {it.family_copy_status === "sent" ? "sent" : "failed"}
                            </>
                          )}
                        </div>
                        {it.delivery_status !== "sent" && (
                          <div className="mt-1 text-xs text-rose-700/80">
                            Email invitation could not be delivered on this
                            attempt — the room and its private link remain
                            available. You can re-send the invitation, or share
                            the private link with the carer directly.
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                  {inviteHistory.carer_first_seen_at && (
                    <li
                      className="flex items-start gap-3"
                      data-testid="cdr-invite-history-carer-entered"
                    >
                      <LogIn className="w-5 h-5 text-purple mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <div>
                          <strong>Carer entered the Room</strong>
                        </div>
                        <div className="text-xs text-ink-soft">
                          {formatTime(inviteHistory.carer_first_seen_at)} &middot;{" "}
                          this is the most reliable signal that your carer has
                          received and used the invitation.
                        </div>
                      </div>
                    </li>
                  )}
                </ol>
              )}
            </Panel>
          )}

          {/* 3. Discussion Log */}
          <Panel eyebrow="3 · Discussion Log" title="Auto-saved conversation" testid="cdr-section-messages">
            <div ref={msgSectionRef}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  {unreadCount > 0 ? (
                    <span
                      className="pill bg-rose-50 text-rose-700"
                      data-testid="cdr-unread-badge"
                    >
                      <Bell className="w-3.5 h-3.5" />
                      {unreadCount} new
                    </span>
                  ) : (
                    <span className="text-xs text-ink-soft flex items-center gap-1">
                      <BellOff className="w-3.5 h-3.5" /> All caught up
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={toggleSound}
                  className={`text-xs flex items-center gap-1.5 rounded-full px-3 py-1.5 border transition
                    ${soundEnabled
                      ? "bg-teal-50 border-teal/40 text-teal font-medium"
                      : "bg-slate-50 border-slate-200 text-ink-muted hover:text-ink"}`}
                  data-testid="cdr-sound-toggle"
                  title={soundEnabled ? "Turn message sound off" : "Turn message sound on"}
                >
                  {soundEnabled ? (
                    <><Volume2 className="w-4 h-4" /> Message sound on</>
                  ) : (
                    <><VolumeX className="w-4 h-4" /> Message sound off</>
                  )}
                </button>
              </div>
              <div
                ref={msgListRef}
                className="rounded-xl bg-slate-50 border border-slate-200 max-h-96 overflow-y-auto p-4 space-y-3"
                data-testid="cdr-msg-list"
              >
                {timeline.length === 0 ? (
                  <p className="text-sm text-ink-soft text-center py-8">No messages yet. Start the conversation below.</p>
                ) : timeline.map((it) => (
                  it.kind === "msg" ? (
                    <div
                      key={`m-${it.payload.message_id}`}
                      data-testid={`cdr-msg-${it.payload.message_id}`}
                      className={it.payload.is_important
                        ? "rounded-lg bg-amber-50 border border-amber-200 p-2 -mx-2"
                        : ""}
                    >
                      <div className="text-xs text-ink-soft flex items-center gap-2 flex-wrap">
                        <strong className="text-ink">{it.payload.sender_name}</strong>
                        <span>· {it.payload.sender_role} · {formatTime(it.payload.created_at)}</span>
                        {it.payload.is_important && (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wide
                                       bg-amber-100 text-amber-900 rounded-full px-2 py-0.5"
                            data-testid={`cdr-msg-important-badge-${it.payload.message_id}`}
                          >
                            Important
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleImportant(it.payload)}
                          className="ml-auto text-[11px] text-ink-soft hover:text-amber-800 underline decoration-dotted"
                          data-testid={`cdr-msg-mark-important-${it.payload.message_id}`}
                        >
                          {it.payload.is_important ? "Unmark important" : "Mark as important"}
                        </button>
                      </div>
                      <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap">{it.payload.body}</p>
                    </div>
                  ) : (
                    <div
                      key={`e-${it.payload.event_id}`}
                      className="my-2 flex items-center gap-2 text-xs text-ink-soft italic"
                      data-testid={`cdr-event-${it.payload.event_id}`}
                    >
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="px-2 py-1 rounded-full bg-slate-100 text-ink-muted whitespace-nowrap">
                        {renderEventLine(it.payload)}
                      </span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>
                  )
                ))}
                <div ref={msgEndRef} />
              </div>
              {!readOnly && (
                <form onSubmit={sendMessage} className="mt-4 flex gap-2" data-testid="cdr-msg-form">
                  <input
                    value={draftMsg}
                    onChange={(e) => setDraftMsg(e.target.value)}
                    placeholder="Type a message…"
                    maxLength={5000}
                    data-testid="cdr-msg-input"
                    className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                  />
                  <button type="submit" disabled={sending || !draftMsg.trim()} className="btn-teal !py-2.5" data-testid="cdr-msg-send">
                    <Send className="w-4 h-4" /> {sending ? "…" : "Send"}
                  </button>
                </form>
              )}
              <p className="mt-3 text-xs text-ink-soft flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-teal" />
                <span data-testid="cdr-chat-autosave">
                  Saved automatically
                  {savedAt.chat && <> · last saved {formatClock(savedAt.chat)}</>}
                </span>
              </p>
            </div>
          </Panel>

          {/* 4. Agreement Checklist */}
          <Panel eyebrow="4 · Agreement Checklist" title="Discuss before agreeing" testid="cdr-section-checklist">
            <p className="text-sm text-ink-muted mb-5">
              Not discussed / Discussed / Needs follow-up are simple working statuses either of you can set anytime.
              An item only shows <strong>✓ Agreed by both</strong> once you've each independently confirmed it.
              {savedAt.checklist && (
                <span className="ml-1 text-xs text-teal" data-testid="cdr-checklist-autosave">
                  Saved automatically · last saved {formatClock(savedAt.checklist)}
                </span>
              )}
            </p>
            {Object.entries(
              checklist.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {})
            ).map(([category, items]) => (
              <div key={category} className="mb-6 last:mb-0">
                <h3 className="font-display font-semibold text-base mb-3">{category}</h3>
                <ul className="space-y-2">
                  {items.map((item) => (
                    <ChecklistItemRow
                      key={item.item_id}
                      item={item}
                      role={role}
                      readOnly={readOnly}
                      busy={!!checklistBusy[item.item_id]}
                      draftValue={checklistDraftDetail[item.item_id]}
                      onDraftChange={(id, val) => setChecklistDraftDetail((d) => ({ ...d, [id]: val }))}
                      onOrgStatus={updateOrgStatus}
                      onPropose={proposeAgreement}
                      onConfirm={confirmAgreement}
                      onDispute={disputeAgreement}
                      onHistory={openHistory}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </Panel>

          {/* 5. Key Decisions */}
          <Panel eyebrow="5 · Key Decisions" title="Short summaries of what's been agreed" testid="cdr-section-decisions">
            {savedAt.decisions && (
              <p className="text-xs text-teal mb-3" data-testid="cdr-decisions-autosave">
                Saved automatically · last saved {formatClock(savedAt.decisions)}
              </p>
            )}
            {!readOnly && (
              <form onSubmit={addDecision} className="space-y-3 mb-6" data-testid="cdr-decision-form">
                <input
                  value={decisionForm.topic}
                  onChange={(e) => setDecisionForm({ ...decisionForm, topic: e.target.value })}
                  placeholder="Topic — e.g. Breaks (required)"
                  data-testid="cdr-decision-topic-input"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                />
                <textarea
                  value={decisionForm.decision}
                  onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value })}
                  placeholder="Decision summary — e.g. Carer to take a 2-hour break daily between 2pm and 4pm where possible. (required)"
                  rows={2}
                  data-testid="cdr-decision-summary-input"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                />
                <div className="flex flex-wrap gap-2">
                  <input
                    value={decisionForm.agreed_by}
                    onChange={(e) => setDecisionForm({ ...decisionForm, agreed_by: e.target.value })}
                    placeholder="Agreed by (optional)"
                    className="flex-1 min-w-[200px] rounded-xl border border-slate-200 px-4 py-2.5 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                  />
                  <button type="submit" className="btn-teal" data-testid="cdr-decision-submit">Record decision</button>
                </div>
              </form>
            )}
            {decisions.length === 0 ? (
              <p className="text-sm text-ink-soft text-center py-6">No decisions recorded yet.</p>
            ) : (
              <ul className="space-y-3">
                {decisions.map((d) => (
                  <li key={d.decision_id} className="rounded-xl border border-slate-200 p-4" data-testid={`cdr-decision-${d.decision_id}`}>
                    <div className="font-display font-semibold">{d.topic}</div>
                    <p className="mt-1 text-sm text-ink leading-relaxed whitespace-pre-wrap">{d.decision}</p>
                    <p className="mt-2 text-xs text-ink-soft">
                      {d.agreed_by && <>Agreed by: {d.agreed_by} · </>}
                      {formatDate(d.created_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* 6. Filing Cabinet (post-activation) / Draft Documents (pre-activation) */}
          <Panel
            eyebrow={isActivated ? "6 · Filing Cabinet" : "6 · Draft Documents"}
            title={isActivated ? "Shared documents by folder" : "Draft documents only"}
            testid="cdr-section-docs"
          >
            {isActivated ? (
              <p className="text-sm text-ink-muted mb-4">
                Uploads land in the folder you choose. Max 10&nbsp;MB per file.
                Allowed: PDF, DOC, DOCX, XLSX, JPG, PNG. Use the &ldquo;Archive&rdquo;
                action to hide a document rather than deleting it.
              </p>
            ) : (
              <p className="text-sm text-ink-muted mb-4">
                This is the temporary draft area, not the full Filing Cabinet. Max 10&nbsp;MB per
                file. Allowed: PDF, DOC, DOCX, XLSX, JPG, PNG.
              </p>
            )}
            {!readOnly && (
              <div
                className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4"
                data-testid="cdr-fc-safety-notice"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-semibold text-sm text-amber-900">
                      {FC_SAFETY_TITLE}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-amber-900">
                      {FC_SAFETY_BODY}
                    </p>
                  </div>
                </div>
                <label className="mt-3 flex items-start gap-2 text-xs font-medium text-amber-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fcSafetyAck}
                    onChange={(e) => setFcSafetyAck(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-amber-400 text-teal focus:ring-teal"
                    data-testid="cdr-fc-safety-checkbox"
                  />
                  <span>{FC_SAFETY_CHECKBOX_LABEL}</span>
                </label>
                <Link
                  to="/wim/care-notes-gdpr-independent-carer"
                  className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-amber-900 underline hover:text-amber-700"
                  data-testid="cdr-fc-safety-article-link"
                >
                  Unsure what care information you should record or keep? Read Why It Matters:
                  Care Notes, GDPR and the Independent Carer
                  <ArrowRight className="w-3.5 h-3.5 shrink-0" />
                </Link>
              </div>
            )}
            {!readOnly && (
              <div className="flex flex-wrap items-center gap-3">
                {isActivated && (
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-ink-muted">Upload to:</span>
                    <select
                      value={uploadFolder}
                      onChange={(e) => setUploadFolder(e.target.value)}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                      data-testid="cdr-upload-folder-select"
                    >
                      {FOLDER_ORDER.filter((f) => f !== "archive").map((f) => (
                        <option key={f} value={f}>{FOLDER_META[f].label}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label
                  className={`btn-outline cursor-pointer ${uploading || !fcSafetyAck ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload document"}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xlsx,.jpg,.jpeg,.png"
                    className="hidden"
                    data-testid="cdr-doc-upload"
                    disabled={!fcSafetyAck}
                    onChange={(e) => uploadDoc(e.target.files?.[0])}
                  />
                </label>
              </div>
            )}
            {documents.length === 0 ? (
              <p className="text-sm text-ink-soft text-center py-6">
                {isActivated ? "No documents yet." : "No draft documents yet."}
              </p>
            ) : !isActivated ? (
              // Pre-activation: original flat list, unchanged.
              <ul className="mt-4 space-y-2" data-testid="cdr-doc-list">
                {documents.map((d) => (
                  <li key={d.document_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3" data-testid={`cdr-doc-${d.document_id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{d.title}</div>
                      <div className="text-xs text-ink-soft">
                        {d.filename} · {(d.size_bytes / 1024).toFixed(1)} KB · uploaded by {d.uploaded_by_email} · {formatDate(d.uploaded_at)}
                      </div>
                    </div>
                    <button onClick={() => downloadDoc(d)} className="btn-outline !px-3 !py-2 text-sm" type="button">
                      <Download className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              // Post-activation: grouped by folder + "Original Discussion Documents".
              <div className="mt-6 space-y-6" data-testid="cdr-filing-cabinet">
                {(() => {
                  const originalDocs = documents.filter((d) => isPreActivationDoc(d) && d.folder !== "archive");
                  const groups = FOLDER_ORDER.map((f) => ({
                    folder: f,
                    docs: documents.filter((d) => !isPreActivationDoc(d) && (d.folder || "other_documents") === f),
                  })).filter((g) => g.docs.length > 0);
                  return (
                    <>
                      {originalDocs.length > 0 && (
                        <div data-testid="cdr-fc-original-docs">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-display font-semibold text-sm">Original Discussion Documents</h4>
                            <span className="text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-ink-muted rounded-full px-2 py-0.5">
                              Read-only historic record
                            </span>
                          </div>
                          <p className="text-xs text-ink-soft mb-3">
                            Uploaded during the Discussion phase — preserved as part of the room&rsquo;s history.
                          </p>
                          <ul className="space-y-2">
                            {originalDocs.map((d) => (
                              <li
                                key={d.document_id}
                                className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-3"
                                data-testid={`cdr-doc-${d.document_id}`}
                              >
                                <div className="min-w-0">
                                  <div className="font-medium text-sm truncate">{d.title}</div>
                                  <div className="text-xs text-ink-soft">
                                    {d.filename} · {(d.size_bytes / 1024).toFixed(1)} KB · uploaded by {d.uploaded_by_email} · {formatDate(d.uploaded_at)}
                                  </div>
                                </div>
                                <button onClick={() => downloadDoc(d)} className="btn-outline !px-3 !py-2 text-sm" type="button">
                                  <Download className="w-4 h-4" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {groups.map((g) => (
                        <div key={g.folder} data-testid={`cdr-fc-folder-${g.folder}`}>
                          <h4 className="font-display font-semibold text-sm mb-2">
                            {FOLDER_META[g.folder].label}
                          </h4>
                          {g.folder === "shift_kit_records" && (
                            <p
                              className="mb-3 text-xs leading-relaxed rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900"
                              data-testid="cdr-fc-shift-kit-notice"
                            >
                              {SHIFT_KIT_NOTICE}
                            </p>
                          )}
                          <ul className="space-y-2">
                            {g.docs.map((d) => (
                              <li
                                key={d.document_id}
                                className={`flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 ${g.folder === "archive" ? "opacity-70" : ""}`}
                                data-testid={`cdr-doc-${d.document_id}`}
                              >
                                <div className="min-w-0">
                                  <div className="font-medium text-sm truncate">{d.title}</div>
                                  <div className="text-xs text-ink-soft">
                                    {d.filename} · {(d.size_bytes / 1024).toFixed(1)} KB · uploaded by {d.uploaded_by_email} · {formatDate(d.uploaded_at)}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => downloadDoc(d)}
                                    className="btn-outline !px-3 !py-2 text-sm"
                                    type="button"
                                    aria-label="Download"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  {!readOnly && g.folder !== "archive" && (
                                    <button
                                      onClick={() => moveDocument(d, "archive")}
                                      className="text-xs text-ink-soft hover:text-rose-700 underline decoration-dotted"
                                      type="button"
                                      data-testid={`cdr-doc-archive-${d.document_id}`}
                                    >
                                      Archive
                                    </button>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </Panel>

          {/* 6b. Shared Agreement Acknowledgement — activated rooms only */}
          {isActivated && (
            <Panel
              eyebrow="6b · Shared Agreement"
              title="Confirm you've read and agreed the Care Agreement"
              testid="cdr-section-ack"
            >
              <p className="text-sm text-ink-muted leading-relaxed">
                This is a shared acknowledgement, not a legal e-signature. Both the
                family/client representative and the invited carer are asked to
                confirm they have read the agreement. Each acknowledgement is
                timestamped and kept in the audit history.
              </p>

              {/* Existing acknowledgements list */}
              {acknowledgements.length > 0 && (
                <ul className="mt-5 space-y-3" data-testid="cdr-ack-list">
                  {acknowledgements.map((a) => (
                    <li
                      key={a.ack_id}
                      className="rounded-xl border border-teal/30 bg-teal-50/40 p-4"
                      data-testid={`cdr-ack-${a.user_role}`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-sm">
                          <strong>{a.user_full_name}</strong>
                          <span className="text-ink-soft"> · {a.user_role} · {formatTime(a.acknowledged_at)}</span>
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-teal/15 text-teal rounded-full px-2 py-0.5">
                          Agreement {a.agreement_version || "v1"}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-ink whitespace-pre-wrap">
                        {a.typed_ack_text}
                      </p>
                      {a.history && a.history.length > 0 && (
                        <details className="mt-2 text-xs text-ink-soft">
                          <summary className="cursor-pointer">
                            {a.history.length} earlier acknowledgement{a.history.length === 1 ? "" : "s"} in audit history
                          </summary>
                          <ul className="mt-2 space-y-1 pl-4 list-disc">
                            {a.history.map((h, i) => (
                              <li key={i}>
                                {h.user_full_name} · {formatTime(h.acknowledged_at)} · &quot;{h.typed_ack_text?.slice(0, 80)}{h.typed_ack_text?.length > 80 ? "…" : ""}&quot;
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {/* Form — only shown to actual participants (not admin observers). */}
              {!readOnly && (role === "family" || role === "carer") && (
                <form
                  onSubmit={submitAcknowledgement}
                  className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-4"
                  data-testid="cdr-ack-form"
                >
                  <p className="text-sm leading-relaxed text-ink italic">
                    I confirm that I have read and agreed the Care Agreement and that
                    this Care Partnership Room will be used as the shared workspace
                    for important communications, agreed documents and practical
                    records connected with this arrangement.
                  </p>
                  <div>
                    <label className="block text-xs font-medium text-ink-muted mb-1">Your full name</label>
                    <input
                      required maxLength={200}
                      value={ackForm.user_full_name}
                      onChange={(e) => setAckForm((f) => ({ ...f, user_full_name: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                      data-testid="cdr-ack-fullname"
                      placeholder="e.g. Amanda Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-muted mb-1">Typed acknowledgement</label>
                    <textarea
                      required rows={3} maxLength={2000}
                      value={ackForm.typed_ack_text}
                      onChange={(e) => setAckForm((f) => ({ ...f, typed_ack_text: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                      data-testid="cdr-ack-text"
                      placeholder="I have read and agreed the Care Agreement…"
                    />
                  </div>
                  <label className="flex items-start gap-2 text-sm text-ink-muted cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={ackForm.read_confirmation}
                      onChange={(e) => setAckForm((f) => ({ ...f, read_confirmation: e.target.checked }))}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-teal focus:ring-teal"
                      data-testid="cdr-ack-checkbox"
                    />
                    <span>I confirm I have read and agreed the Care Agreement.</span>
                  </label>
                  <button
                    type="submit"
                    disabled={ackSubmitting || !ackForm.read_confirmation}
                    className="btn-teal"
                    data-testid="cdr-ack-submit"
                  >
                    {ackSubmitting ? "Recording…" : "Record my acknowledgement"}
                  </button>
                  <p className="text-xs text-ink-soft">
                    Re-submitting will replace your visible acknowledgement with the
                    latest one; your earlier entries remain in the audit history.
                  </p>
                </form>
              )}
            </Panel>
          )}

          {/* 7. Activate */}
          <Panel eyebrow="7 · Activate Care Partnership Room" title="Family/Client: Activate Care Partnership Room" testid="cdr-section-activate">
            {isActivated ? (
              <div className="rounded-xl bg-purple/10 border border-purple/30 p-4 text-sm">
                <CheckCircle2 className="w-5 h-5 text-purple inline-block mr-2" />
                Activated on {formatDate(room.activated_at)}. The full Care Partnership Room
                is now available above.
              </div>
            ) : role === "carer" ? (
              <div className="space-y-3 text-ink-muted text-sm leading-relaxed" data-testid="cdr-carer-activate-note">
                <p>
                  Activation is the family/client representative&rsquo;s decision. When you
                  are both ready to move from planning to a live working arrangement,
                  they can activate the full Care Partnership Room from their view of
                  this page.
                </p>
                <p>
                  Once activated, this room unlocks the Shared Agreement, Communications,
                  Filing Cabinet folders and Placement State for both of you.
                </p>
                {invite?.message && (
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => copyText(invite.message)}
                    data-testid="cdr-carer-copy-reminder"
                  >
                    <Copy className="w-4 h-4" /> Copy activation reminder
                  </button>
                )}
              </div>
            ) : role === "family" ? (
              <>
                <p className="text-ink-muted leading-relaxed text-sm">
                  When you and your carer have talked through the key terms and are ready
                  to work together, activate the full Care Partnership Room. This unlocks
                  the shared workspace for the term of your arrangement — Shared Agreement,
                  Communications, Filing Cabinet folders and Placement State.
                </p>
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setShowActivateConfirm(true)}
                    className="btn-purple"
                    data-testid="cdr-activate-btn"
                  >
                    Activate Care Partnership Room
                  </button>
                </div>
              </>
            ) : role === "admin" ? (
              <>
                <p className="text-ink-muted leading-relaxed text-sm">
                  Admin activation is available as a support fallback. In the normal flow
                  the family/client representative activates their own room from this page.
                </p>
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setShowActivateConfirm(true)}
                    className="btn-outline !py-2 text-sm"
                    data-testid="cdr-admin-activate"
                  >
                    Admin: mark as activated
                  </button>
                </div>
              </>
            ) : null}
          </Panel>

          {/* 7b. Placement State — post-activation lifecycle controls
              (family/admin only; carer sees current state read-only). */}
          {isActivated && room.status !== "archived" && (
            <Panel
              eyebrow="7b · Placement state"
              title="Where this arrangement is right now"
              testid="cdr-section-placement-state"
            >
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-ink-muted">Current status:</span>
                <span
                  className={`pill ${
                    room.status === "activated" ? "bg-teal-50 text-teal" :
                    room.status === "under_review" ? "bg-amber-50 text-amber-800" :
                    room.status === "ending_soon" ? "bg-rose-50 text-rose-700" :
                    "bg-slate-100 text-ink-muted"
                  }`}
                  data-testid="cdr-current-placement-state"
                >
                  {statusLabel(room.status)}
                </span>
              </div>
              {(role === "family" || role === "admin") ? (
                <>
                  <p className="text-sm text-ink-muted leading-relaxed mb-4">
                    Move the room between <strong>Active</strong>, <strong>Under Review</strong>
                    and <strong>Ending Soon</strong> to reflect where the arrangement is.
                    These are reversible. Archiving is terminal — the room becomes read-only
                    and cannot be re-opened.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "activated",    label: "Mark as Active"       },
                      { key: "under_review", label: "Mark as Under Review" },
                      { key: "ending_soon",  label: "Mark as Ending Soon"  },
                    ].map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        disabled={statusChanging || room.status === s.key}
                        onClick={() => changeStatus(s.key)}
                        className={`btn-outline text-sm ${room.status === s.key ? "opacity-50 pointer-events-none" : ""}`}
                        data-testid={`cdr-status-${s.key}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-ink-muted leading-relaxed" data-testid="cdr-carer-status-note">
                  The family/client representative sets the placement state. If you&rsquo;d like
                  the room moved to <strong>Ending Soon</strong> or <strong>Archived</strong>,
                  send a message in the Communications area above to request it.
                </p>
              )}
            </Panel>
          )}

          {/* 8. Expiry / Status */}
          <Panel eyebrow="8 · Status" title="Room status & expiry" testid="cdr-section-status">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Status</div><div className="mt-1"><StatusPill status={room.status} /></div></div>
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Activation</div><div className="mt-1 font-medium">{room.activated_at ? formatDate(room.activated_at) : "Not activated"}</div></div>
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Created</div><div className="mt-1 font-medium">{formatDate(room.created_at)}</div></div>
              <div><div className="text-ink-soft text-xs uppercase tracking-wide">Expires</div><div className="mt-1 font-medium">{formatDate(room.expires_at)}{remaining !== null && room.status === "discussion" && <span className="ml-2 text-xs text-ink-soft">({remaining} day{remaining === 1 ? "" : "s"} left)</span>}</div></div>
            </div>
            <p className="mt-5 text-sm text-ink-muted leading-relaxed">
              This Care Discussion Room is designed for initial agreement only. Once both
              parties agree to work together, activate the full Care Partnership Room to
              continue using shared records, care logs, documents and communication tools.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/care-rooms"
                className="btn-outline text-sm"
                data-testid="cdr-back-to-my-rooms"
              >
                <ArrowLeft className="w-4 h-4" /> Back to My Rooms
              </Link>
              {role === "family" && room.status === "discussion" && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm("Archive this room? It will become read-only.")) return;
                    try {
                      await api.post(`/api/care-rooms/${roomId}/archive`);
                      toast.success("Room archived");
                      refreshRoom();
                    } catch (err) {
                      toast.error(formatErr(err));
                    }
                  }}
                  className="btn-outline text-sm"
                  data-testid="cdr-archive-btn"
                >
                  <Archive className="w-4 h-4" /> Archive room
                </button>
              )}
            </div>
          </Panel>
        </div>
      </div>

      {/* Floating chat launcher — appears when the Discussion Log has
          scrolled off-screen, so replies are always one tap away without
          scrolling back to section 3. Renders the SAME messages + input
          state as the main Discussion Log (no duplicate storage). */}
      {chatOffScreen && !showFloatingChat && (
        <button
          type="button"
          onClick={() => {
            setShowFloatingChat(true);
            setUnreadCount(0);
            setTimeout(() => floatingInputRef.current?.focus(), 150);
          }}
          className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6
                     rounded-full shadow-lg bg-teal text-white hover:bg-teal/90
                     px-4 py-3 flex items-center gap-2 text-sm font-medium"
          data-testid="cdr-floating-chat-launcher"
          aria-label="Open Discussion Log"
        >
          <MessageCircle className="w-5 h-5" strokeWidth={2} />
          <span className="hidden sm:inline">Discussion Log</span>
          {unreadCount > 0 && (
            <span
              className="rounded-full bg-rose-500 text-white text-xs font-semibold
                         min-w-[1.5rem] h-6 px-2 flex items-center justify-center"
              data-testid="cdr-floating-chat-badge"
            >
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Floating chat modal (compact panel).
          Desktop: right-side slide-in card. Mobile: bottom sheet. */}
      {showFloatingChat && (
        <div
          className="fixed inset-0 z-40 flex sm:items-end sm:justify-end"
          role="dialog"
          aria-modal="true"
          aria-label="Discussion Log"
          data-testid="cdr-floating-chat-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-[1px] sm:bg-black/20"
            onClick={() => setShowFloatingChat(false)}
            aria-hidden="true"
          />
          {/* Panel */}
          <div
            className="relative w-full sm:w-[400px] sm:mr-4 sm:mb-4
                       bg-white border border-slate-200 rounded-t-2xl sm:rounded-2xl
                       shadow-2xl flex flex-col
                       max-h-[80vh] sm:max-h-[70vh] mt-auto sm:mt-0"
            data-testid="cdr-floating-chat-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-teal" />
                <div>
                  <div className="text-sm font-semibold">Discussion Log</div>
                  <div className="text-xs text-ink-soft">Same conversation · saved automatically</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowFloatingChat(false)}
                className="text-ink-soft hover:text-ink rounded-full p-1"
                data-testid="cdr-floating-chat-close"
                aria-label="Close Discussion Log"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50"
              data-testid="cdr-floating-chat-list"
              ref={(el) => {
                // Scroll to bottom whenever the timeline grows while open.
                if (el) el.scrollTop = el.scrollHeight;
              }}
            >
              {timeline.length === 0 ? (
                <p className="text-sm text-ink-soft text-center py-8">
                  No messages yet. Say hello below.
                </p>
              ) : timeline.map((it) => (
                it.kind === "msg" ? (
                  <div
                    key={`fm-${it.payload.message_id}`}
                    data-testid={`cdr-floating-msg-${it.payload.message_id}`}
                  >
                    <div className="text-[11px] text-ink-soft">
                      <strong className="text-ink">{it.payload.sender_name}</strong>
                      {" · "}{formatTime(it.payload.created_at)}
                    </div>
                    <p className="mt-0.5 text-sm leading-relaxed whitespace-pre-wrap">{it.payload.body}</p>
                  </div>
                ) : (
                  <div
                    key={`fe-${it.payload.event_id}`}
                    className="my-1 flex items-center gap-2 text-[11px] text-ink-soft italic"
                    data-testid={`cdr-floating-event-${it.payload.event_id}`}
                  >
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-ink-muted whitespace-nowrap">
                      {renderEventLine(it.payload)}
                    </span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                )
              ))}
            </div>

            {!readOnly ? (
              <form
                onSubmit={sendMessage}
                className="flex gap-2 p-3 border-t border-slate-100 bg-white"
                data-testid="cdr-floating-chat-form"
              >
                <input
                  ref={floatingInputRef}
                  value={draftMsg}
                  onChange={(e) => setDraftMsg(e.target.value)}
                  placeholder="Type a message…"
                  maxLength={5000}
                  data-testid="cdr-floating-chat-input"
                  className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm
                             focus:border-teal focus:ring-1 focus:ring-teal outline-none"
                />
                <button
                  type="submit"
                  disabled={sending || !draftMsg.trim()}
                  className="btn-teal !py-2 !px-3"
                  data-testid="cdr-floating-chat-send"
                >
                  <Send className="w-4 h-4" /> {sending ? "…" : "Send"}
                </button>
              </form>
            ) : (
              <div className="p-3 text-xs text-ink-soft border-t border-slate-100 bg-white">
                This room is read-only. New messages are not accepted.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checklist history modal */}
      {historyItem && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setHistoryItem(null)}>
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`checklist-history-modal-${historyItem.item_id}`}>
            <h3 className="font-display text-xl font-semibold">History — {historyItem.label}</h3>
            {historyLoading ? (
              <p className="mt-4 text-sm text-ink-muted flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
            ) : historyRows.length === 0 ? (
              <p className="mt-4 text-sm text-ink-muted">No activity recorded yet.</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {historyRows.map((h) => (
                  <li key={h.history_id} className="text-sm border-b border-slate-100 pb-3 last:border-0" data-testid={`checklist-history-row-${h.history_id}`}>
                    <div className="text-xs text-ink-soft">
                      {h.changed_at ? formatTime(h.changed_at) : "—"} · {h.changed_by_email || "system"} ({h.changed_by_role || "n/a"}) · {h.event_type}
                    </div>
                    <div className="mt-0.5 text-ink-muted">
                      {h.previous_status !== h.new_status && <>Status: {h.previous_status || "—"} → {h.new_status || "—"}<br /></>}
                      {h.previous_agreement_state !== h.new_agreement_state && <>State: {h.previous_agreement_state || "—"} → {h.new_agreement_state || "—"}<br /></>}
                      {h.new_agreed_detail && <>Detail: &ldquo;{h.new_agreed_detail}&rdquo;</>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-6 flex justify-end">
              <button type="button" onClick={() => setHistoryItem(null)} className="btn-outline" data-testid="checklist-history-close-btn">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Activation confirmation modal */}
      {showActivateConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowActivateConfirm(false)}>
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="cdr-activate-confirm">
            <h3 className="font-display text-xl font-semibold">Activate Care Partnership Room</h3>
            <p className="mt-3 text-ink-muted text-sm leading-relaxed">
              This will flip the room from Discussion into an active Care Partnership Room
              for both of you. Once activated, it unlocks:
            </p>
            <ul className="mt-3 text-ink-muted text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li>The Shared Agreement acknowledgement</li>
              <li>Communications with an &ldquo;important&rdquo; flag</li>
              <li>The Filing Cabinet with folders</li>
              <li>Placement State tracking</li>
            </ul>
            <p className="mt-3 text-ink-muted text-sm leading-relaxed">
              You can still archive the room later if things change.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowActivateConfirm(false)} className="btn-outline" data-testid="cdr-activate-cancel-btn">Cancel</button>
              <button type="button" onClick={confirmActivation} disabled={activating} className="btn-teal" data-testid="cdr-activate-confirm-btn">
                {activating ? "Activating…" : "Activate room"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
